import React, { useCallback, useEffect, useState } from "react";
import { Table, Input, Select, Space, Tag, Tooltip, Button, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotate, faFileCsv, faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import rewardProgramApi from "../../../api/rewardProgramApi";
import {
    orderStatusMeta, rewardAmountTitle, CAMP_LABELS, fmtMoney, fmtNumber, fmtUnix, totalsByStatus, basisColumns,
} from "./reward-shared-meta";

const SORTS = [
    { value: "purchaseTime", label: "Mua sớm nhất trước" },
    { value: "purchaseTimeDesc", label: "Mua mới nhất trước" },
    { value: "reward", label: "Thưởng cao nhất" },
];
const DEFAULT_QUERY = { search: "", status: "", campId: "", batchDate: "", sort: "purchaseTime", page: 1, pageSize: 50 };

/**
 * Tab Phân bổ đơn: sổ RewardOrders của đợt.
 * Trước đây kéo tối đa 2.000 dòng về FE rồi lọc tại chỗ — đợt 10/8 có ~2.500 đơn
 * nên bảng nặng và không tìm được đơn cụ thể. Nay lọc + phân trang ở server,
 * tổng theo trạng thái luôn tính trên TOÀN đợt (không đổi theo bộ lọc).
 */
function RewardAllocationsTab({ program }) {
    const programId = program?.id;
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [totals, setTotals] = useState([]);
    const [options, setOptions] = useState({ batchDates: [], camps: [] });
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [query, setQuery] = useState(DEFAULT_QUERY);

    const fetchRows = useCallback(async () => {
        if (!programId) return;
        setLoading(true);
        try {
            const { data } = await rewardProgramApi.allocations(programId, query);
            setRows(data.rows || []);
            setTotal(data.total || 0);
            setTotals(data.totals || []);
            setOptions(data.options || { batchDates: [], camps: [] });
        } catch {
            message.error("Không tải được dữ liệu phân bổ");
        } finally {
            setLoading(false);
        }
    }, [programId, query]);

    useEffect(() => { fetchRows(); }, [fetchRows]);

    const patch = (next) => setQuery((q) => ({ ...q, page: 1, ...next }));

    // Export lấy TOÀN BỘ đợt theo bộ lọc từ server (không chỉ trang đang xem).
    const exportCsv = async () => {
        setExporting(true);
        try {
            const { data } = await rewardProgramApi.allocationsCsv(programId, { ...query, page: undefined, pageSize: undefined });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(data);
            a.download = `reward-${program.slug}-${query.status || "all"}.csv`;
            a.click();
            URL.revokeObjectURL(a.href);
        } catch {
            message.error("Export thất bại");
        } finally {
            setExporting(false);
        }
    };

    const byStatus = totalsByStatus(totals);
    // Chữ trạng thái + tên cột tiền theo LOẠI ĐỢT (đợt ứng trước không phát thưởng).
    const STATUS_META = orderStatusMeta(program);

    const columns = [
        {
            title: "Đơn", dataIndex: "orderId", width: 190, fixed: "left",
            render: (v) => (
                <Space size={4}>
                    <span className="font-mono text-xs">{v}</span>
                    <Tooltip title="Mở đơn ở trang Đơn hàng">
                        <a href={`/order?orderId=${encodeURIComponent(v)}`} target="_blank" rel="noreferrer">
                            <FontAwesomeIcon icon={faUpRightFromSquare} className="text-content-3" />
                        </a>
                    </Tooltip>
                </Space>
            ),
        },
        {
            title: "Người mua", dataIndex: "userId", width: 190,
            render: (v, r) => (
                <div className="leading-tight">
                    <div>{r.userName || `User ${v}`}</div>
                    <Space size={4}>
                        <span className="text-xs text-content-3">#{v}</span>
                        <Tooltip title="Xem đơn của user này">
                            <a className="text-xs" href={`/order?userId=${v}`} target="_blank" rel="noreferrer">đơn</a>
                        </Tooltip>
                    </Space>
                </div>
            ),
        },
        { title: "Sàn", dataIndex: "campId", width: 100, render: (v) => CAMP_LABELS[v] || v },
        { title: "Mua lúc", dataIndex: "purchaseTime", width: 145, render: (v) => fmtUnix(v, "DD/MM HH:mm:ss") },
        { title: "Lô", dataIndex: "settleBatchDate", width: 105 },
        ...basisColumns(program),
        {
            title: rewardAmountTitle(program), dataIndex: "rewardAmount", width: 105, align: "right",
            render: (v, r) => <b className={r.status === "awarded" ? "text-green-600" : ""}>{fmtMoney(v)}</b>,
        },
        {
            // Ref trên TIỀN THƯỞNG (10% Shopee / 5% sàn khác) — KHÔNG phải "HH ref" ở trang Đơn hàng
            // (ref trên hoa hồng đơn). Tooltip cộng đủ 2 khoản người giới thiệu nhận từ đơn này.
            title: <Tooltip title="Hoa hồng giới thiệu tính trên tiền thưởng — khác cột HH ref (trên hoa hồng đơn) ở trang Đơn hàng">Ref của thưởng</Tooltip>,
            dataIndex: "refRewardAmount", width: 120, align: "right",
            render: (v, r) => {
                if (!r.refUserId) return "—";
                const orderRef = r.orderRefCommission;
                const paidNote = r.refBonusId ? "đã cộng ví" : "chưa cộng ví — chờ tiền hoàn đơn vào ví";
                const tip = [
                    `Người giới thiệu #${r.refUserId}`,
                    orderRef != null
                        ? `HH ref đơn ${fmtMoney(orderRef)} + ref thưởng ${fmtMoney(v)} = ${fmtMoney(Number(orderRef) + Number(v || 0))}`
                        : `Ref thưởng ${fmtMoney(v)}`,
                    `Ref thưởng ${paidNote}`,
                ].join(" · ");
                return <Tooltip title={tip}>{fmtMoney(v)}</Tooltip>;
            },
        },
        {
            title: "Trạng thái", dataIndex: "status", width: 125,
            render: (v, r) => (
                <Space direction="vertical" size={0}>
                    <Tag color={STATUS_META[v]?.color} style={{ marginInlineEnd: 0 }}>{STATUS_META[v]?.label || v}</Tag>
                    {r.bonusId ? <span className="text-xs text-content-3">Bonus #{r.bonusId}</span> : null}
                </Space>
            ),
        },
        {
            title: "Lý do", dataIndex: "reason", width: 220, ellipsis: true,
            render: (v, r) => (v ? <Tooltip title={r.reasonHint || v}>{r.reasonText || v}</Tooltip> : "—"),
        },
    ];

    return (
        <div className="flex flex-col gap-3">
            <Space wrap>
                <Input.Search
                    allowClear placeholder="Tìm mã đơn hoặc userId" style={{ width: 260 }}
                    onSearch={(v) => patch({ search: v })}
                />
                <Select
                    value={query.status} onChange={(v) => patch({ status: v })} style={{ width: 170 }}
                    options={[{ value: "", label: "Tất cả trạng thái" },
                    ...Object.entries(STATUS_META).map(([v, m]) => ({ value: v, label: m.label }))]}
                />
                <Select
                    value={query.campId} onChange={(v) => patch({ campId: v })} style={{ width: 140 }}
                    options={[{ value: "", label: "Tất cả sàn" }, ...options.camps.map((c) => ({ value: c, label: CAMP_LABELS[c] || c }))]}
                />
                <Select
                    value={query.batchDate} onChange={(v) => patch({ batchDate: v })} style={{ width: 150 }}
                    options={[{ value: "", label: "Tất cả lô ngày" }, ...options.batchDates.map((d) => ({ value: d, label: d }))]}
                />
                <Select value={query.sort} onChange={(v) => patch({ sort: v })} options={SORTS} style={{ width: 185 }} />
                <Button icon={<FontAwesomeIcon icon={faRotate} />} onClick={fetchRows}>Tải lại</Button>
                <Button icon={<FontAwesomeIcon icon={faFileCsv} />} loading={exporting} onClick={exportCsv}>
                    Export CSV (toàn bộ)
                </Button>
            </Space>
            <Space wrap size={4}>
                {Object.entries(STATUS_META).map(([key, meta]) => (
                    <Tag
                        key={key} color={query.status === key ? meta.color : undefined}
                        className="cursor-pointer" onClick={() => patch({ status: query.status === key ? "" : key })}
                    >
                        {meta.label}: {fmtNumber(byStatus[key]?.count)} đơn · {fmtMoney(byStatus[key]?.amount)}
                    </Tag>
                ))}
            </Space>
            <Table
                size="small" rowKey="id" dataSource={rows} columns={columns} loading={loading}
                scroll={{ x: "max-content" }}
                pagination={{
                    current: query.page, pageSize: query.pageSize, total, showSizeChanger: true,
                    pageSizeOptions: [50, 100, 200, 500], showTotal: (t) => `${fmtNumber(t)} đơn khớp bộ lọc`,
                    onChange: (page, pageSize) => setQuery((q) => ({ ...q, page, pageSize })),
                }}
            />
        </div>
    );
}

export default RewardAllocationsTab;
